import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  Radio,
  Typography,
} from "@mui/material";

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  } catch {
    return null;
  }
};

const Cell = ({ label, value }) => {
  const shown = value === undefined || value === null || value === "" ? "—" : String(value);
  return (
    <Grid item xs={6}>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, lineHeight: 1.3, wordBreak: "break-word" }}
        title={shown}
      >
        {shown}
      </Typography>
    </Grid>
  );
};

/**
 * One account in a duplicate group — compact 2-column field grid, radio for keeper.
 */
const AccountDetailCard = ({
  account,
  entityType,
  isKeeper,
  isRecommended,
  onSelect,
}) => {
  const accountId =
    entityType === "users"
      ? account.user_id
      : entityType === "law_firms"
      ? account.lawfirm_id
      : account.pg_id;

  const title = (() => {
    if (entityType === "users") {
      const fullName = [account.first_name, account.last_name].filter(Boolean).join(" ");
      return fullName || account.username || account.email || `User ${accountId}`;
    }
    if (entityType === "law_firms") {
      return account.name || `Law Firm ${accountId}`;
    }
    return account.full_name || account.business_name || `PG ${accountId}`;
  })();

  const subtitle = (() => {
    if (entityType === "users") return account.email;
    if (entityType === "law_firms") return account.customer_number ? `Customer #${account.customer_number}` : null;
    return account.email_address;
  })();

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1.5,
        border: isKeeper ? "2px solid #2563eb" : "1px solid rgba(0,0,0,0.12)",
        backgroundColor: isKeeper ? "rgba(37,99,235,0.04)" : "#ffffff",
        height: "100%",
      }}
    >
      <CardContent sx={{ py: 1.25, px: 1.5, "&:last-child": { pb: 1.25 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 1 }}>
          <FormControlLabel
            sx={{ m: 0, alignItems: "flex-start" }}
            control={
              <Radio
                size="small"
                checked={!!isKeeper}
                onChange={() => onSelect && onSelect(accountId)}
                sx={{ py: 0 }}
              />
            }
            label={
              <Box sx={{ pt: 0.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            }
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
            <Chip size="small" label={`ID ${accountId}`} variant="outlined" sx={{ height: 22 }} />
            {isRecommended ? (
              <Chip size="small" label="Recommended" color="success" variant="outlined" sx={{ height: 22 }} />
            ) : null}
            {account.deleted_at ? (
              <Chip size="small" label="Soft-deleted" color="warning" variant="outlined" sx={{ height: 22 }} />
            ) : null}
          </Box>
        </Box>

        {entityType === "users" && (
          <Grid container spacing={0.75} columnSpacing={1.5}>
            <Cell label="Username" value={account.username} />
            <Cell label="Role" value={account.user_role} />
            <Cell label="Law firm" value={account.law_firm_name} />
            <Cell label="Position" value={account.lawfirm_user_position} />
            <Cell
              label="Bonds (req / firm)"
              value={`${account.bonds_as_requester || 0} / ${account.bonds_as_lawfirm_user || 0}`}
            />
            <Cell label="PG record" value={account.pg_full_name || (account.pg_id ? `pg_id ${account.pg_id}` : null)} />
            <Cell label="Last login" value={formatDate(account.last_login)} />
            <Cell label="Created" value={formatDate(account.created_at)} />
            <Cell label="Tokens" value={account.token_count} />
          </Grid>
        )}

        {entityType === "law_firms" && (
          <Grid container spacing={0.75} columnSpacing={1.5}>
            <Cell label="Customer number" value={account.customer_number} />
            <Cell label="Active" value={account.is_active ? "Yes" : "No"} />
            <Cell
              label="Bonds (own / managing)"
              value={`${account.bond_count || 0} / ${account.managing_bond_count || 0}`}
            />
            <Cell label="Locations" value={account.location_count} />
            <Cell label="User mappings" value={account.user_mapping_count} />
            <Cell label="Created" value={formatDate(account.created_at)} />
            <Cell label="Updated" value={formatDate(account.updated_at)} />
          </Grid>
        )}

        {entityType === "pgs" && (
          <Grid container spacing={0.75} columnSpacing={1.5}>
            <Cell label="Business name" value={account.business_name} />
            <Cell label="Email" value={account.email_address} />
            <Cell label="Phone" value={account.cell_phone} />
            <Cell label="Portal username" value={account.portal_username} />
            <Cell label="Portal email" value={account.portal_email} />
            <Cell label="Total bonds" value={account.total_bonds} />
            <Cell label="Bond guardians" value={account.bond_guardian_count} />
            <Cell label="Blanket bonds" value={account.blanket_bond_count} />
            <Cell label="PG contacts" value={account.pg_contact_count} />
            <Cell label="Created" value={formatDate(account.created_at)} />
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountDetailCard;
